import { ClocksService } from './clocks.service';
import { ClockType } from './../_shared/models/enums/clock-type.enum';
import { ClockName } from './../_shared/models/enums/clock-name.enum';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonModal, IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Match } from '../_shared/models/match.model';
import { TimePickerComponent } from '../_shared/components/time-picker/time-picker.component';
import { take } from 'rxjs';
import { ToastService } from '../_core/services/toast.service';


@Component({
  standalone: true,
  selector: 'app-clocks',
  templateUrl: './clocks.component.html',
  styleUrls: ['./clocks.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    IonicModule,
    CommonModule,
    TranslateModule,
    TimePickerComponent
  ]
})
export class ClocksComponent {
  private modalController = inject(ModalController);
  private clocksService = inject(ClocksService);
  private datePipe = inject(DatePipe);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  @ViewChild(IonModal) modal: IonModal;

  public readonly clockName : typeof ClockName = ClockName;
  public readonly clockType: typeof ClockType = ClockType;

  form = new FormGroup({
    name: new FormControl(ClockName.WARM_UP, Validators.required),
    type: new FormControl(ClockType.TIMER, Validators.required),
    defaultValue: new FormControl(1, Validators.required),
    displayValue: new FormControl('0:00', Validators.required),
    value: new FormControl(''),
    isPaused: new FormControl(true, Validators.required),
    timePipe: new FormControl('m:ss', Validators.required),
    adjustTime: new FormControl('-1000', Validators.required),
    timeoutPerMatch: new FormControl('3', Validators.required),
    timeoutPerSet: new FormControl('1', Validators.required),
  }, { validators: [ClocksComponent.timeoutValidator] });
  

  @Input({ required: true }) match: Match;
  timeoutOptions = Array.from({length: 10}, (_, i) => i + 1);

  static timeoutValidator(group: FormGroup) {
    const name = group.get('name')?.value;
    const timeoutPerMatch = +group.get('timeoutPerMatch')?.value || 0;
    const timeoutPerSet = +group.get('timeoutPerSet')?.value || 0;
    if (name === ClockName.TIMEOUT && timeoutPerMatch < timeoutPerSet) {
      return { timeoutInvalid: true };
    }
  
    return null;
  }
  
  async dismiss() {
    this.modalController.dismiss(null, 'cancel');
  }

  closeAddClock(ev) {
    this.modal.dismiss(null, 'cancel');
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
        if (controls[name].invalid) {
            invalid.push(name);
        }
    }
    return invalid;
}

  save() {
    if (this.form.invalid) {
      if (this.form.errors.timeoutInvalid) {
      this.toastService.presentDangerToast(this.translateService.instant('Timeout per match must be greater than or equal to timeout per set..'));
      }
      else {
        this.toastService.presentDangerToast(this.translateService.instant('Please fill all required fields.'));
      }
      return;
    };
    this.clocksService.add(this.match._id, this.form.value)
      .pipe(take(1))
      .subscribe(
        match => {
          this.match.clocks = match.clocks;
          this.modal.dismiss(match.clocks);
        }
    )
  }

  async deleteClock(clockId: string) {
    this.clocksService.remove(this.match._id, clockId).subscribe(
      () => {
        const index = this.match.clocks.findIndex(c => c._id === clockId);
        this.match.clocks.splice(index, 1);
      }
    )
  }

  updateClock(value) {
    this.form.controls.defaultValue.setValue(value);
    this.form.controls.displayValue.setValue(this.datePipe.transform(value, this.form.controls.timePipe.value));
  }
}
